import MainLayout from "@/components/layout/admin/MainLayout.vue";
import Select2Multiple from "v-select2-multiple-component";
import Loading from "vue-loading-overlay";
import "vue-loading-overlay/dist/vue-loading.css";
import axios from "axios";
import { required } from "vuelidate/lib/validators";
import ValidationService from "@/services/ValidationService";

const MONTH_OPTIONS = [
  { id: 1, text: "Jan" },
  { id: 2, text: "Feb" },
  { id: 3, text: "Mar" },
  { id: 4, text: "Apr" },
  { id: 5, text: "May" },
  { id: 6, text: "Jun" },
  { id: 7, text: "Jul" },
  { id: 8, text: "Aug" },
  { id: 9, text: "Sep" },
  { id: 10, text: "Oct" },
  { id: 11, text: "Nov" },
  { id: 12, text: "Dec" },
];

export default {
  name: "FerryZoneGeneratePayment",
  components: {
    MainLayout,
    Select2Multiple,
    Loading,
  },
  mixins: [ValidationService],
  data() {
    return {
      isLoading: false,
      submitted: false,
      acdYearOptions: [],
      zoneOptions: [],
      studentOptions: [],
      monthOptions: MONTH_OPTIONS,
      formData: {
        acd_year_id: null,
        from_month: null,
        to_month: null,
        zone_id: null,
        student_ids: [],
      },
    };
  },
  validations: {
    formData: {
      acd_year_id: { required },
      from_month: { required },
      to_month: { required },
      zone_id: { required },
      student_ids: { required },
    },
  },
  watch: {
    "formData.acd_year_id"() {
      this.resetClientValidation();
      this.resetStudents();
      this.loadStudents();
    },
    "formData.zone_id"() {
      this.resetClientValidation();
      this.resetStudents();
      this.loadStudents();
    },
    "formData.from_month"() {
      this.resetClientValidation();
    },
    "formData.to_month"() {
      this.resetClientValidation();
    },
    "formData.student_ids": {
      handler() {
        this.resetClientValidation();
      },
      deep: true,
    },
  },
  methods: {
    clearValidationErrors() {
      this.validationErrors = {};
    },

    resetClientValidation() {
      this.submitted = false;
      this.clearValidationErrors();
      this.$nextTick(() => {
        this.$v.$reset();
      });
    },

    handleError(error) {
      if (error.response && error.response.status == 401) {
        this.$store.commit("logout");
        window.location.replace("login");
      }
    },

    resetStudents() {
      this.formData.student_ids = [];
      this.studentOptions = [];
    },

    async getAcdYearList() {
      try {
        this.isLoading = true;
        const response = await axios.get("score-setting/parent-acd-year");
        if (response.data) {
          const rawData = response.data.data || [];
          this.acdYearOptions = rawData.map((item) => ({
            id: item.id,
            text: item.period_name,
          }));
          const activeId = response.data.active_acd_year_id;
          this.formData.acd_year_id = activeId ?? this.acdYearOptions?.[0]?.id ?? null;
        }
      } catch (error) {
        this.handleError(error);
      } finally {
        this.isLoading = false;
      }
    },

    async getZoneList() {
      try {
        this.isLoading = true;
        const response = await axios.get("ferry-zone/list");
        const zones = Array.isArray(response.data) ? response.data : [];
        this.zoneOptions = zones.map((zone) => ({
          id: zone.id,
          text: zone.zone_name,
        }));
      } catch (error) {
        this.handleError(error);
      } finally {
        this.isLoading = false;
      }
    },

    async loadStudents() {
      if (!this.formData.acd_year_id || !this.formData.zone_id) {
        return;
      }

      try {
        this.isLoading = true;
        const response = await axios.get("ferry-zone/students", {
          params: {
            acd_year_id: this.formData.acd_year_id,
            zone_id: this.formData.zone_id,
          },
        });
        const data = Array.isArray(response.data)
          ? response.data
          : (response.data?.data || []);
        const students = data.map((item) => ({
          id: item.id,
          text: item.text ?? item.name,
        }));

        if (students.length > 0) {
          this.studentOptions = [
            { id: "all", text: "All" },
            ...students,
          ];
        } else {
          this.studentOptions = [
            { id: "", text: "No students available" },
          ];
        }
      } catch (error) {
        this.handleError(error);
        this.studentOptions = [];
      } finally {
        this.isLoading = false;
      }
    },

    getSelectedStudentIds() {
      if (this.formData.student_ids.includes("all")) {
        return this.studentOptions
          .filter((s) => s.id !== "all" && s.id !== "")
          .map((s) => s.id);
      }
      return [...this.formData.student_ids];
    },

    cancel() {
      this.$router.push({ name: "ferryZoneList" });
    },

    buildPayload(skipExisting = false) {
      return {
        acd_year_id: this.formData.acd_year_id,
        from_month: Number(this.formData.from_month),
        to_month: Number(this.formData.to_month),
        zone_id: this.formData.zone_id,
        student_ids: this.getSelectedStudentIds(),
        skip_existing: skipExisting,
      };
    },

    async submit(skipExisting = false) {
      if (typeof skipExisting !== "boolean") {
        skipExisting = false;
      }

      this.submitted = true;
      this.$v.$touch();
      if (this.$v.$invalid) {
        return;
      }

      const studentIds = this.getSelectedStudentIds();
      if (!studentIds.length) {
        this.$notification.error("At least one student must be selected.", {
          timer: 3,
          position: "bottomCenter",
        });
        return;
      }

      this.isLoading = true;
      this.clearValidationErrors();

      try {
        const response = await axios.post(
          "ferry-zone/generate-payment",
          this.buildPayload(skipExisting)
        );

        this.$notification.success(response.data.message || "Ferry payments generated successfully.", {
          timer: 3,
          position: "bottomCenter",
        });

        const ferryItemId = response.data.ferry_item_id;
        this.$router.push({
          name: "paymentList",
          query: ferryItemId ? { item_id: ferryItemId } : {},
        });
      } catch (error) {
        this.handleError(error);

        if (error.response && error.response.status == 422) {
          const data = error.response.data || {};

          if (data.warning) {
            const confirmed = confirm(data.message);
            if (confirmed) {
              await this.submit(true);
            }
            return;
          }

          this.printValidationError(error.response);

          const hasFieldErrors = Object.keys(this.validationErrors || {}).length > 0;
          if (!hasFieldErrors) {
            const message = data.message
              || (data.errors && Object.values(data.errors).flat()[0])
              || "Unable to generate ferry payments.";
            this.$notification.error(message, {
              timer: 3,
              position: "bottomCenter",
            });
          }
          return;
        }

        this.$notification.error(
          error.response?.data?.message || error.response?.data?.error || "Unable to generate ferry payments.",
          {
            timer: 3,
            position: "bottomCenter",
          }
        );
      } finally {
        this.isLoading = false;
      }
    },
  },
  mounted() {
    this.getAcdYearList();
    this.getZoneList();
  },
};
